import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ClearanceGoToHelper } from "@/lib/utility/clearance.go-to-helper";
import { formatCurrency } from "@/lib/utility/currency-helper";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import { $t } from "@/lib/utility/t";
import clearanceRequestService from "@/services/mrfiktiv/services/clearance-request.service";
import clearanceService from "@/services/mrfiktiv/services/clearance.service";
import {
  MrfiktivByAtDtoGen,
  MrfiktivClearanceApprovalViewModelGen,
  MrfiktivClearanceViewModelGen,
  MrfiktivCreateActivityLogDtoGen,
  MrfiktivCreateClearanceDtoGen,
  MrfiktivCreateClearanceResponseDtoGen,
  MrfiktivInvoiceLineViewModelGen,
  MrfiktivRepairEstimateViewModelGen,
  MrfiktivStatusHistoryElementViewModelGen,
  MrfiktivUpdateClearanceDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { ClearanceDataAccessLayer } from "@/store/modules/access-layers/clearance.access-layer";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import VueRouter from "vue-router";
import { ActivityLog, IActivityLog } from "./activity-log.entity";
import { ClearanceApproval, IClearanceApproval } from "./clearance-approval.entity";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { IReference, Reference } from "./reference.entity";
import { IReport, Report } from "./report.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { ISharedContent, SharedContent } from "./shared-content.entity";
import sharedContentService from "@/services/mrfiktiv/services/sharedContentService";
import { PageFilterElement } from "./page-filter-element.entity";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { SharedContentDataAccessLayer } from "@/store/modules/access-layers/shared-content.access-layer";
import { IBaseImage } from "./caseEntity";
import { ClearanceApprovalDataAccessLayer } from "@/store/modules/access-layers/clearance-approval.access-layer";

@IsFilterable
class ClearanceBase implements IEntity<IClearance, MrfiktivClearanceViewModelGen>, MrfiktivClearanceViewModelGen {
  /** Id of the clearance object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.id"
  })
  id: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.clearance.status",
    config: {
      items: Object.values(RequestStatusEnum).map(status => ({
        value: status,
        text: `enums.ClearanceRequestStatusEnum.${status}`
      })),
      itemValue: "value"
    }
  })
  status: RequestStatusEnum;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.clearance.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId: string;

  /**
   * The title of the object
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.title"
  })
  title?: string;

  get titleReadable() {
    if (!this.title) {
      return $t("contract.noTitle");
    }

    return this.title;
  }

  /**
   * The description of the object
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.description"
  })
  description?: string;

  /**
   * ID of the related estimate
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.estimateId"
  })
  estimateId?: string;

  /**
   * ID of the related report
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.documentId",
    config: {
      itemCallback: () => ReportDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-report"
    }
  })
  reportId?: string;
  report: IReport | null = null;

  /**
   * ID of the related appraisal
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.appraisalId"
  })
  appraisalId?: string;

  /**
   * Array of clearance approval IDs that this clearance is based on
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.clearance.clearanceApprovalIds"
  })
  clearanceApprovalIds: string[];

  isClearanceApprovalLoading = false;
  clearanceApprovals: IClearanceApproval[] = [];

  /**
   * Start date for repair work
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.repairStart"
  })
  repairStart?: string | null;

  get repairStartFormable(): string {
    if (!this.repairStart) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.repairStart));
  }

  set repairStartFormable(date: string) {
    if (date) {
      this.repairStart = new Date(date).toISOString();
    } else {
      this.repairStart = undefined;
    }
  }

  /**
   * End date for repair work
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.repairEnd"
  })
  repairEnd?: string | null;

  get repairEndFormable(): string {
    if (!this.repairEnd) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.repairEnd));
  }

  set repairEndFormable(date: string) {
    if (date) {
      this.repairEnd = new Date(date).toISOString();
    } else {
      this.repairEnd = undefined;
    }
  }

  /**
   * total
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.clearance.total"
  })
  total?: number;

  /**
   * subTotal
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.clearance.subTotal"
  })
  subTotal?: number;

  get subTotalReadable() {
    if (this.subTotal === undefined || this.subTotal === null) {
      return "";
    }
    return formatCurrency(this.subTotal, this.currency);
  }

  /**
   * taxTotal
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.clearance.taxTotal"
  })
  taxTotal?: number;

  get totalReadable() {
    if (this.total === undefined || this.total === null) {
      return "";
    }
    return formatCurrency(this.total, this.currency);
  }

  /** LineItems - if applicable */
  lineItems: IInvoiceLine[];

  /** Buyer - if applicable */
  buyer?: IInvoiceParty;

  /** Seller - if applicable */
  @FilterConfig({
    type: InvoiceParty,
    displayName: "objects.clearance.seller"
  })
  seller?: IInvoiceParty;

  @FilterConfig({
    type: Reference
  })
  refs: IReference[];

  get refsCategories(): BackendResourceEnum[] {
    return [];
  }

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.clearance.currency",
    config: {
      items: Object.values(CurrencyEnum)
    }
  })
  currency: CurrencyEnum;

  loading = false;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  history: MrfiktivStatusHistoryElementViewModelGen[];

  @FilterConfig({
    displayName: "objects.clearance.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  assignees?: string[];

  isSharedContentLoading = false;
  sharedContent: ISharedContent[] = [];

  images?: IBaseImage[];

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearance.externalId"
  })
  externalId?: string | undefined;

  costExtensionEstimateId?: string | undefined;
  costExtensionEstimate?: MrfiktivRepairEstimateViewModelGen | null | undefined;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.clearance.isCleared"
  })
  isCleared: boolean;

  cleared?: MrfiktivByAtDtoGen | undefined;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.clearance.clearanceStatus"
  })
  clearanceStatus: "cleared" | "finished" | "created" | "sent" | "delivered";

  clearanceHistory: MrfiktivStatusHistoryElementViewModelGen[];
  isUpdateable?: boolean | undefined;

  get lastSharedContent(): ISharedContent | undefined {
    if (!this.sharedContent.length) {
      return undefined;
    }

    return this.sharedContent[0];
  }

  get totalGross() {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems.reduce((acc, item) => acc + (1 + item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get totalNet() {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems.reduce((acc, item) => acc + item.amount * item.quantity, 0);
  }

  get totalTax() {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems.reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get taxRates() {
    if (!this.lineItems || this.lineItems.length === 0) return [];
    return Array.from(new Set(this.lineItems.map(line => line.taxRate))).filter(rate => rate !== 0);
  }

  get accpetedDate(): string | null {
    const acceptedEntry = this.history.find(h => h.status === RequestStatusEnum.ACCEPTED);
    if (acceptedEntry) {
      return acceptedEntry.at;
    }
    return null;
  }

  get periodInDays() {
    if (!this.repairStart || !this.repairEnd) {
      return;
    }

    const startDate = new Date(this.repairStart);
    const endDate = new Date(this.repairEnd);
    const daysCount = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysCount;
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  static get statusColorMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "grey"],
      [RequestStatusEnum.REQUESTED, "orange lighten-2"],
      [RequestStatusEnum.REPLIED, "green"],
      [RequestStatusEnum.ACCEPTED, "primary"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "red"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "red"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "orange"],
      [RequestStatusEnum.FINISHED, "deep-purple lighten-1"]
    ]);
  }

  static get statusIconMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUESTED, "mdi-file-document-outline"],
      [RequestStatusEnum.REPLIED, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "mdi-message-question-outline"],
      [RequestStatusEnum.ACCEPTED, "mdi-check-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "mdi-close-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "mdi-close-circle-outline"],
      [RequestStatusEnum.FINISHED, "mdi-check-bold"]
    ]);
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusColor() {
    return ClearanceBase.statusColorMap.get(this.status);
  }

  /**
   * Icon - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusIcon() {
    return ClearanceBase.statusIconMap.get(this.status);
  }

  get statusReadable() {
    return $t(`enums.ClearanceRequestStatusEnum.${this.status}`);
  }

  /**
   * Is the clearance completed
   */
  get isCompleted() {
    return [
      RequestStatusEnum.REPLIED,
      RequestStatusEnum.REJECTED_BY_REQUESTED,
      RequestStatusEnum.REJECTED_BY_REQUESTING,
      RequestStatusEnum.ACCEPTED,
      RequestStatusEnum.FINISHED
    ].includes(this.status);
  }

  /**
   * Returns whether all clearance approvals have been successfully completed.
   *
   * This getter checks the loading state and the completion status of all entries
   * in the `clearanceApprovals` array. If the data is still loading, it returns `false`.
   * If any approval is not completed successfully, it also returns `false`.
   * Only if all approvals are marked as completed successfully, it returns `true`.
   *
   * @returns {boolean} `true` if all approvals are completed successfully and not loading; otherwise `false`.
   *
   */
  get allApprovalsCleared() {
    if (this.isClearanceApprovalLoading) {
      return false;
    }

    for (const approval of this.clearanceApprovals) {
      if (!approval.isCompletedSuccessfully) {
        return false;
      }
    }

    return true;
  }

  /**
   * Is the clearance approved
   */
  get isApproved() {
    return [RequestStatusEnum.ACCEPTED, RequestStatusEnum.REPLIED, RequestStatusEnum.REQUESTED].includes(this.status);
  }

  /**
   * Is the clearance completed successfully
   */
  get isCompletedSuccessfully() {
    return [RequestStatusEnum.REPLIED, RequestStatusEnum.ACCEPTED].includes(this.status);
  }

  get isSend() {
    return this.status !== RequestStatusEnum.DRAFT;
  }

  get canUpdate() {
    return [RequestStatusEnum.DRAFT, RequestStatusEnum.REQUESTED].includes(this.status);
  }

  constructor(data?: Partial<MrfiktivClearanceViewModelGen | IClearance>) {
    this.id = data?.id ?? "";
    this.partnerId = data?.partnerId ?? "";
    this.userId = data?.userId ?? "";
    this.reportId = data?.reportId ?? "";
    this.report = data?.report ? new Report(data?.report) : null;

    this.isCleared = data?.isCleared ?? false;
    this.cleared = data?.cleared;
    this.clearanceStatus = data?.clearanceStatus ?? "created";
    this.clearanceHistory = data?.clearanceHistory ?? [];
    this.costExtensionEstimateId = data?.costExtensionEstimateId;
    this.costExtensionEstimate = data?.costExtensionEstimate;

    this.title = data?.title;
    this.description = data?.description;
    this.estimateId = data?.estimateId;
    this.appraisalId = data?.appraisalId;
    this.clearanceApprovalIds = data?.clearanceApprovalIds ?? [];
    this.clearanceApprovals = ((data?.clearanceApprovals as MrfiktivClearanceApprovalViewModelGen[]) ?? []).map(
      ca => new ClearanceApproval(ca)
    );
    this.repairStart = data?.report?.repair?.startDate ?? data?.repairStart;
    this.repairEnd = data?.report?.repair?.endDate ?? data?.repairEnd;

    this.total = data?.total;
    this.subTotal = data?.subTotal;
    this.taxTotal = data?.taxTotal;
    this.timestamp = new Timestamp(data?.timestamp);
    this.lineItems = ((data?.lineItems as MrfiktivInvoiceLineViewModelGen[]) ?? []).map(line => new InvoiceLine(line));
    this.buyer = data?.buyer ? new InvoiceParty(data?.buyer as IInvoiceParty) : undefined;
    this.seller = data?.seller ? new InvoiceParty(data?.seller as IInvoiceParty) : undefined;
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map(ref => new Reference(ref as IReference)));
    this.status = (data?.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.currency = (data?.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.history = data?.history ?? [];
    this.assignees = data?.assignees ?? [];
    this.images = data?.images ?? [];
  }

  map(data: MrfiktivClearanceViewModelGen | IClearance): void {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.reportId = data.reportId ?? "";
    this.report = data?.report ? new Report(data?.report) : null;

    this.title = data.title;
    this.description = data.description;
    this.estimateId = data.estimateId;
    this.appraisalId = data.appraisalId;
    this.clearanceApprovalIds = data.clearanceApprovalIds ?? [];
    this.clearanceApprovals = ((data?.clearanceApprovals as MrfiktivClearanceApprovalViewModelGen[]) ?? []).map(
      ca => new ClearanceApproval(ca)
    );

    this.repairStart = data?.report?.repair?.startDate ?? data?.repairStart;
    this.repairEnd = data?.report?.repair?.endDate ?? data?.repairEnd;

    this.total = data.total;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = ((data.lineItems as MrfiktivInvoiceLineViewModelGen[]) ?? []).map(line => new InvoiceLine(line));
    this.buyer = data.buyer ? new InvoiceParty(data.buyer as IInvoiceParty) : undefined;
    this.seller = data.seller ? new InvoiceParty(data.seller as IInvoiceParty) : undefined;
    this.refs = Reference.filterDuplicates((data.refs ?? []).map(ref => new Reference(ref as IReference)));
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.history = data.history ?? [];
    this.assignees = data.assignees ?? [];
    this.images = data?.images ?? [];
  }

  /**
   * Calculates all the line items for a given taxRate
   * @param taxRate the taxRate to calculate tax for
   * @returns
   */
  calcItemsPerTaxRate(taxRate: number): number {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems
      .filter(l => l.taxRate === taxRate)
      .reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  goTo(router: VueRouter) {
    return {
      table: () => new ClearanceGoToHelper(router).goToClearanceTable({ partnerId: this.partnerId }),
      detail: () =>
        new ClearanceGoToHelper(router).goToClearanceDetail({
          partnerId: this.partnerId,
          clearanceId: this.id
        })
    };
  }

  async create(): Promise<this> {
    this.loading = true;
    try {
      const createDto: Partial<MrfiktivCreateClearanceDtoGen> = {
        title: this.title,
        description: this.description,
        estimateId: this.estimateId,
        reportId: this.reportId,
        appraisalId: this.appraisalId,
        clearanceApprovalIds: this.clearanceApprovalIds,
        repairStart: this.repairStart,
        repairEnd: this.repairEnd,
        lineItems: this.lineItems.map(item => item.dto),
        seller: this.seller?.dto,
        buyer: this.buyer?.dto,
        refs: Reference.filterDuplicates(this.refs),
        currency: this.currency,
        total: this.total,
        subTotal: this.subTotal,
        taxTotal: this.taxTotal,
        assignees: this.assignees
      };
      const res = await clearanceService.create(this.partnerId, createDto as MrfiktivCreateClearanceDtoGen);

      this.map(res as MrfiktivClearanceViewModelGen);
      ClearanceDataAccessLayer.set(this);

      await this.fetchReportAndVehicle();
    } finally {
      this.loading = false;
    }

    await Promise.all([this.addCreateActivitiesOnReferences()]);

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await clearanceService.getOne(this.partnerId, this.id);
      this.map(res as MrfiktivClearanceViewModelGen);
      ClearanceDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      ClearanceDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateClearanceDtoGen): Promise<this> {
    const res = await clearanceService.update(this.partnerId, this.id, dto);

    this.map(res as MrfiktivClearanceViewModelGen);
    ClearanceDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      const updateDto: MrfiktivUpdateClearanceDtoGen = {
        title: this.title,
        description: this.description,
        estimateId: this.estimateId,
        reportId: this.reportId,
        appraisalId: this.appraisalId,
        clearanceApprovalIds: this.clearanceApprovalIds,
        repairStart: this.repairStart,
        repairEnd: this.repairEnd,
        lineItems: this.lineItems.map(item => item.dto),
        seller: this.seller?.dto,
        buyer: this.buyer?.dto,
        refs: Reference.filterDuplicates(this.refs),
        currency: this.currency,
        total: this.total,
        subTotal: this.subTotal,
        taxTotal: this.taxTotal,
        assignees: this.assignees
      };
      await this.updatePartial(updateDto);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  async delete(): Promise<void> {
    this.loading = true;
    try {
      const res = await clearanceService.delete(this.partnerId, this.id);

      this.map(res as MrfiktivClearanceViewModelGen);
      ClearanceDataAccessLayer.delete(this);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
  }

  async updateStatus(status: RequestStatusEnum): Promise<this> {
    this.loading = true;
    try {
      const updated = await clearanceService.updateStatus(this.partnerId, this.id, { status });

      this.map(updated as MrfiktivClearanceViewModelGen);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  /**
   * Adds activity logs celebrating the creation of the clearance
   * @returns the created activity logs
   */
  async addCreateActivitiesOnReferences(): Promise<IActivityLog[]> {
    const sources = [...this.refs];
    if (
      this.estimateId &&
      !sources.some(ref => ref.refType === BackendResourceEnum.REPAIR_ESTIMATE && ref.refId === this.estimateId)
    ) {
      sources.push(
        new Reference({
          refType: BackendResourceEnum.REPAIR_ESTIMATE,
          refId: this.estimateId
        })
      );
    }
    if (
      this.reportId &&
      !sources.some(ref => ref.refType === BackendResourceEnum.REPORT && ref.refId === this.reportId)
    ) {
      sources.push(
        new Reference({
          refType: BackendResourceEnum.REPORT,
          refId: this.reportId
        })
      );
    }
    for (const approvalId of this.clearanceApprovalIds) {
      if (!sources.some(ref => ref.refType === BackendResourceEnum.CLEARANCE_APPROVAL && ref.refId === approvalId)) {
        sources.push(
          new Reference({
            refType: BackendResourceEnum.CLEARANCE_APPROVAL,
            refId: approvalId
          })
        );
      }
    }
    const target = {
      refType: BackendResourceEnum.CLEARANCE,
      refId: this.id
    };

    const activityAsync = [];

    for (const source of sources) {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: source,
        target: [target],
        actionType: ActionEnum.CREATE,
        activity: ActivityTypeEnum.CREATE_REF
      };

      activityAsync.push(
        new ActivityLog({
          partnerId: this.partnerId,
          ...data
        }).create()
      );
    }

    return Promise.all(activityAsync);
  }

  /**
   * Create an activity log for assignee changes
   * @param newAssignees The ids of the new assignees of the ticket
   * @param activityType Whether the assignee was added or removed
   */
  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: BackendResourceEnum.CLEARANCE,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }

  async respondAccept(sct: string, data: MrfiktivCreateClearanceResponseDtoGen) {
    this.loading = true;
    try {
      const partnerId = this.partnerId;

      const clearanceId = this.id;

      const res = await clearanceRequestService.respondAccept(partnerId, clearanceId, sct, data);

      this.map(res as MrfiktivClearanceViewModelGen);
      ClearanceDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }
  async fetchReportAndVehicle() {
    this.report = null;

    const reportId = this.reportId;
    if (!reportId) {
      return;
    }

    this.report =
      ReportDataAccessLayer.get(reportId) ||
      (await new Report({
        id: reportId,
        partnerId: this.partnerId
      }).fetch()) ||
      null;

    await this.report?.fetchVehicle();
  }

  async fetchClearanceApprovals() {
    this.isClearanceApprovalLoading = true;

    try {
      const uniqueApprovals = new Map<string, InstanceType<typeof ClearanceApproval>>();
      for (const approvalId of this.clearanceApprovalIds) {
        let approval = ClearanceApprovalDataAccessLayer.get(approvalId);
        if (!approval) {
          approval = await new ClearanceApproval({ id: approvalId, partnerId: this.partnerId }).fetch();
          await approval.fetchSharedContent();
          ClearanceApprovalDataAccessLayer.set(approval);
        }
        await approval.fetchSharedContent();
        uniqueApprovals.set(approval.id, approval);
      }
      this.clearanceApprovals = Array.from(uniqueApprovals.values());
    } finally {
      this.isClearanceApprovalLoading = false;
    }
  }

  async fetchSharedContent() {
    this.isSharedContentLoading = true;
    this.sharedContent = [];

    try {
      const sharedContents = await sharedContentService.getSharedContentPaginated({
        partnerId: this.partnerId,
        filter: [
          new PageFilterElement({
            key: "refs.refId",
            operation: PageFilterOperationEnum.EQUAL,
            value: this.id
          })
        ]
      });

      if (!sharedContents.data) {
        return;
      }

      for (const sharedContent of sharedContents.data) {
        const sharedContentEntity = new SharedContent(sharedContent);
        SharedContentDataAccessLayer.set(sharedContentEntity);
        this.sharedContent.push(sharedContentEntity);
      }
    } finally {
      this.isSharedContentLoading = false;
    }
  }

  /**
   * Set the status to FINISHED
   */
  async finish(): Promise<this> {
    this.loading = true;
    try {
      const updated = await clearanceService.updateStatus(this.partnerId, this.id, {
        status: RequestStatusEnum.FINISHED
      });
      this.map(updated);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
    return this;
  }
}

type IClearance = ClearanceBase;
const Clearance = Filter.createForClass(ClearanceBase);

export { Clearance, IClearance };
